import appConfig from '../../config/app';
import { getAuthHeaders } from './auth';
import { EMPTY_PROGRAM_FILTER } from '../program-filter';

export function getGym(gymUUID) {
  return fetch(`${appConfig.baseApiUrl}/api/v1/gyms/${gymUUID}`)
    .then((response) => response.json())
    .catch((error) => console.error(error));
}

export function getGymByInvite(inviteCode) {
  return fetch(
    `${appConfig.baseApiUrl}/api/v1/gyms/show_by_invite_code/${inviteCode}`
  )
    .then((response) => response.json())
    .catch((error) => console.error(error));
}

export async function createGym(gymData) {
  const options = await getAuthHeaders();
  return fetch(`${appConfig.baseApiUrl}/api/v1/gyms`, {
    method: 'POST',
    headers: {
      ...options,
    },
    body: JSON.stringify({
      ...gymData,
    }),
  })
    .then((response) => response.json())
    .then((data) => data.name)
    .catch((error) => console.error(error));
}

function buildAgeQuery(age) {
  switch (age) {
    case 'adult':
      return '&is_adult=true';
    case 'child':
      return '&is_adult=false';
    default:
      return '';
  }
}

export async function fetchGymUsers(gymUid, searchTerm, params) {
  const options = await getAuthHeaders();
  const {
    age,
    program,
    status,
    inactiveForMonths,
    startDate,
    endDate,
    currentPage,
    perPage = 50,
    sortBy,
    sortOrder,
    queryString,
    csv,
    lastPromoted,
  } = params;

  const searchQuery = searchTerm ? `&search_query=${searchTerm}` : '';
  const enabledDateQueryString =
    endDate && startDate ? `&start_date=${startDate}&end_date=${endDate}` : '';
  const dateQueryString = !lastPromoted ? enabledDateQueryString : '';

  const page = currentPage ? `&page=${currentPage}` : '';
  const perPageQuery = perPage ? `&per_page=${perPage}` : '';
  const programQuery = program ? `&gym_program_id=${program}` : '';
  const ageQuery = age ? buildAgeQuery(age) : '';
  const memberStatus = status ? `&status=${status}` : '';
  const inactivityWindow = status && inactiveForMonths ? `&inactive_for_months=${inactiveForMonths}` : '';
  const sortByQuery = sortBy ? `sort_by=${sortBy}` : 'sort_by=name';
  const sortOrderQuery = sortBy ? `&sort_order=${sortOrder}` : '';
  const csvQuery = csv ? `&format=csv` : '';
  const lastPromotedQuery = lastPromoted
    ? `&filter_active_days_since_last_promotion=${lastPromoted}`
    : '';
  const dynamicQueryString = `${sortByQuery}${sortOrderQuery}${searchQuery}${dateQueryString}${page}${perPageQuery}${programQuery}${ageQuery}${memberStatus}${inactivityWindow}${csvQuery}${lastPromotedQuery}`;
  // This is to check for a query string override
  const finalParamQueryString = queryString
    ? `&${queryString}`
    : dynamicQueryString;

  if (gymUid) {
    try {
      const users = await fetch(
        `${appConfig.baseApiUrl}/api/v1/gyms/${gymUid}/users?${finalParamQueryString}`,
        {
          headers: {
            ...options,
          },
        }
      );

      if (csv) {
        return users;
      }

      const usersRes = await users.json();
      return usersRes;
    } catch (error) {
      console.log(error);
    }
  } else {
    return { error: 'User not authenticated' };
  }
}

const explorerSortField = (sortBy) => {
  switch (sortBy) {
    case 'belt_name':
      return 'belt';
    case 'active_days':
      return 'active_days_since_last_promotion';
    default:
      return sortBy || 'name';
  }
};

const explorerQueryForGymMembers = (searchTerm, params) => {
  const filters = [];
  const { program, startDate, endDate, lastPromoted } = params;

  if (searchTerm && searchTerm.trim()) {
    filters.push({ field: 'name', operator: 'contains', value: searchTerm.trim() });
  }

  if (program === EMPTY_PROGRAM_FILTER) {
    filters.push({ field: 'gym_program_id', operator: 'is_empty' });
  } else if (program) {
    filters.push({ field: 'gym_program_id', operator: 'eq', value: program });
  }

  const query = { version: 1, combinator: 'and', filters };
  if (!lastPromoted && startDate && endDate) {
    query.activity_window = { start: startDate, end: endDate };
  }

  return query;
};

// The legacy Gym Members API calculates every matching member before it
// paginates. This adapter keeps the existing table contract but uses the
// optimized Member Explorer search endpoint instead.
export async function fetchGymMembersFromExplorer(gymUid, searchTerm, params) {
  const result = await searchMemberExplorerUsers(gymUid, {
    query: explorerQueryForGymMembers(searchTerm, params),
    sort: {
      field: explorerSortField(params.sortBy),
      direction: params.sortOrder || 'asc',
    },
    page: params.currentPage || 1,
    per_page: params.perPage || 50,
  });

  return {
    users: result.users.map((member) => ({
      ...member,
      active_days: member.active_days_since_last_promotion,
    })),
    meta: result.meta,
  };
}

export async function fetchGymMembersExplorerCsv(gymUid, searchTerm, params) {
  const firstPage = await fetchGymMembersFromExplorer(gymUid, searchTerm, {
    ...params,
    currentPage: 1,
    perPage: 100,
  });
  const users = firstPage.users.slice();
  const totalPages = firstPage.meta ? firstPage.meta.total_pages : 1;

  for (let page = 2; page <= totalPages; page += 1) {
    const nextPage = await fetchGymMembersFromExplorer(gymUid, searchTerm, {
      ...params,
      currentPage: page,
      perPage: 100,
    });
    users.push(...nextPage.users);
  }

  const cell = (value) => {
    const text = value === null || typeof value === 'undefined' ? '' : String(value);
    return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
  };
  const header = ['Name', 'Belt Name', 'DOB', 'Gym Program', 'Active Days'];
  const rows = users.map((member) => [
    member.name,
    member.belt ? member.belt.name : '',
    member.dob,
    member.gym_program ? member.gym_program.name : '',
    member.active_days,
  ]);

  return [header, ...rows].map((row) => row.map(cell).join(',')).join('\n');
}

export async function fetchMemberExplorerDefinitions(gymUid) {
  const options = await getAuthHeaders();
  const response = await fetch(
    `${appConfig.baseApiUrl}/api/v1/gyms/${gymUid}/member_explorer/definitions`,
    { headers: { ...options } }
  );

  if (!response.ok) {
    throw new Error('Unable to load Member Explorer filters');
  }

  return response.json();
}

export async function searchMemberExplorerUsers(gymUid, payload) {
  const options = await getAuthHeaders();
  const response = await fetch(
    `${appConfig.baseApiUrl}/api/v1/gyms/${gymUid}/member_explorer/search`,
    {
      method: 'POST',
      headers: { ...options },
      body: JSON.stringify(payload),
    }
  );

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || 'Unable to search members');
  }

  return data;
}

export async function runMemberExplorerBulkAction(gymUid, payload) {
  const options = await getAuthHeaders();
  const response = await fetch(
    `${appConfig.baseApiUrl}/api/v1/gyms/${gymUid}/member_explorer/bulk_actions`,
    {
      method: 'POST',
      headers: { ...options },
      body: JSON.stringify(payload),
    }
  );
  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data.error || 'Unable to complete the bulk action');
  }

  return data;
}
